import Book from 'models/Book';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AudioRecorder from 'view/components/common/AudioRecorder';
import parse from 'html-react-parser';
import BookCompletedFooter from './BookCompletedFooter';
import useApiCall from 'contexts/ApiCall';
import ReadingUtils from 'utils/ReadingUtils';
import MissedWord from 'models/MissedWord';
import ReadingFrame from 'view/components/common/ReadingFrame';
import aiService from 'services/aiService';
import Button from 'view/components/buttons/Button';
import {
  evaluateSpeakingAssessment,
  EvaluationResult,
} from 'utils/speakingAssessmentUtils';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import studentService from 'services/studentService';

import 'view/style/student/components/assignment/listeningAssessment.css';

interface SpeakingAssessmentProps {
  assignment: StudentGradebookEntry;
  book: Book;
  isReading: boolean;
  startIndex?: number;
  missedWords?: MissedWord[];
  teacherView?: boolean;
  setIsReading: (isReading: boolean) => void;
  onStopReading: (
    wordIndex: number,
    elapsedTime: number,
    response: EvaluationResult[],
  ) => void;
  onCompletion: (elapsedTime: number, response: EvaluationResult[]) => void;
  onMissedWord: (word: string, index: number, known: boolean) => void;
  onViewQuestions?: () => void;
}

const SpeakingAssessment: React.FC<SpeakingAssessmentProps> = ({
  assignment,
  book,
  isReading,
  startIndex = 0,
  missedWords,
  teacherView = false,
  setIsReading,
  onStopReading,
  onCompletion,
  onMissedWord,
  onViewQuestions,
}) => {
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [words, setWords] = useState<string[]>([]);
  const [wordIndex, setWordIndex] = useState(0);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null); // State to store the audio blob in case of an error
  const [error, setError] = useState<string | null>(null);
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResult[]>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [studentAudioSrc, setStudentAudioSrc] = useState<string | null>(null);
  const makeApiCall = useApiCall();

  const generateTeacherHTML = useCallback(() => {
    if (!evaluationResults) return htmlContent;
    return `<p>${evaluationResults
      .map((result) => {
        let className = '';
        let word;

        // Assign class based on the type
        switch (result.type) {
          case 'match':
            className = 'brw match';
            word = result.refWord;
            break;
          case 'deletion':
            className = 'brw missed';
            word = result.refWord;
            break;
          case 'substitution':
            className = 'brw incorrect';
            word = result.spokenWord;
            break;
          case 'insertion':
            word = result.spokenWord;
            className = 'brw insertion';
            break;
          default:
            word = result.refWord;
            className = 'brw';
        }

        // Return the corresponding div with the word
        return `<span title="${result.type}" className="${className}">${word}</span>`;
      })
      .join(' ')}</p>`;
  }, [htmlContent, evaluationResults]);

  useEffect(() => {
    if (!teacherView || studentAudioSrc) return;

    if (assignment.submission.audio_src) {
      setStudentAudioSrc(assignment.submission.audio_src);
    } else {
      assignment.submission
        .getAudioSrc()
        .then((resp) => setStudentAudioSrc(resp));
    }
    // eslint-disable-next-line
  }, [assignment.submission.audio_url, assignment.submission, teacherView]);

  useEffect(() => {
    if (evaluationResults) {
      if (teacherView) {
        setHtmlContent(generateTeacherHTML());
      }
      return;
    }
    makeApiCall(
      studentService.getAssignmentSubmissionResults,
      assignment.submission.id,
    ).then((resp) => {
      try {
        setEvaluationResults(resp.map((r: any) => JSON.parse(r)));
      } catch {
        setEvaluationResults(resp);
      }
    });
  }, [
    teacherView,
    assignment.submission.id,
    generateTeacherHTML,
    makeApiCall,
    evaluationResults,
  ]);

  useEffect(() => {
    const targetElement = document.getElementById((wordIndex - 1).toString());
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [htmlContent, wordIndex, isReading]);

  useEffect(() => {
    if (!words.length || wordIndex > 0 || !missedWords) return;
    if (startIndex > 0) {
      // mark the reading up until the current word
      const missedWordIdxs: number[] = missedWords.map(
        (word) => word.word_index,
      );
      if (!teacherView) {
        for (let i = 0; i < startIndex; i += 1) {
          highlightWord(!missedWordIdxs.includes(i), i);
        }
      }
    }
    // eslint-disable-next-line
  }, [htmlContent, words, startIndex, missedWords, wordIndex]);

  useEffect(() => {
    if (!book || words.length > 0 || !book.html_content) return;
    const bookWords = book.words ?? book.getWords();
    setHtmlContent(ReadingUtils.createHtmlString(bookWords, book.html_content));
    setWords(bookWords);
  }, [book, words.length]);

  const evaluateAssignment = (convertedSpeech: string) => {
    const results = evaluateSpeakingAssessment(
      convertedSpeech,
      book.words ?? book.getWords(),
      wordIndex,
    );

    // mark up the reading
    var updatedIndex = wordIndex;
    results.forEach((res: EvaluationResult) => {
      if (res.index !== null) {
        highlightWord(res.correct, res.index);
        updatedIndex = res.index;
      }
    });

    // now submit all missed words
    try {
      handleMissedWords(results);
    } catch (error) {
      console.error(error);
    }

    if (updatedIndex >= book.getWords().length - 1) {
      setIsCompleted(true);
      onCompletion(0, [...(evaluationResults ?? []), ...results]);
    } else {
      onStopReading(updatedIndex, 0, [
        ...(evaluationResults ?? []),
        ...results,
      ]);
    }

    console.debug(results);
    return results;
  };

  async function handleMissedWords(results: EvaluationResult[]) {
    for (const { refWord, index, correct } of results) {
      if (refWord !== null && index !== null && !correct) {
        const alreadyMissed = missedWords?.some(
          (mw) => mw.word === refWord && mw.word_index === index,
        );
        if (!alreadyMissed) {
          onMissedWord(refWord, index, true);
          await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay
        }
      }
    }
  }

  const submitAudio = async (audioBlob: Blob) => {
    const audio_upload_url = `assignments/submissions/${assignment.submission.id}/response.wav`;
    makeApiCall(
      aiService.convertSpeechToText,
      audioBlob,
      'en-US',
      false,
      audio_upload_url,
    )
      .then((resp) => evaluateAssignment(resp))
      .catch((error) => {
        setError(error.message); // Set the error message
        setAudioBlob(audioBlob); // Store the audio blob to allow resubmission
      });
  };

  const highlightWord = useCallback(
    (isCorrect: boolean, wordIndex: number) => {
      const wordNode = containerRef.current?.querySelector('.brw.current');
      if (wordNode) {
        wordIndex = Number(wordNode.id.split('-')[1]);
        wordNode.className = `brw${isCorrect ? ' read' : ' incorrect'}`;
      }

      const nextWordNode = containerRef.current?.querySelector(
        `#word-${wordIndex + 1}`,
      );
      if (nextWordNode) {
        nextWordNode.className = `brw current`;
      }

      setWordIndex(wordIndex + 1);
    },
    [containerRef, setWordIndex],
  );

  return (
    <div className="reading-container">
      <ReadingFrame refLink={containerRef}>{parse(htmlContent)}</ReadingFrame>
      {isCompleted || teacherView ? (
        <BookCompletedFooter
          onViewQuestions={onViewQuestions}
          submissionAudio={studentAudioSrc}
          teacherView={teacherView}
        />
      ) : (
        <div className="row">
          {!assignment.submission.date_completed && (
            <AudioRecorder
              onStartRecording={() => setIsReading(true)}
              onStopRecording={() => setIsReading(false)}
              onSubmit={submitAudio}
              startConfirmationMessage="Once you start, you must finish the reading. Are you ready?"
              endConfirmationMessage="Once you stop, you cannot try again. Did you finish the reading?"
            />
          )}
          {error && audioBlob && (
            <div>
              <span className="label-small danger">Error: {error}</span>
              <Button
                label="Resubmit"
                text="resubmit"
                onClick={() => submitAudio(audioBlob)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeakingAssessment;
